/**
 * DisputeWizard Component
 * Architecture v1 - Dispute Workflow Integration
 * 
 * Multi-step wizard for creating disputes with:
 * - AI-suggested reasons based on account classification
 * - User override/selection from reason library
 * - Real-time workflow resolution (routing_strategy, template_family, prompt_keys)
 * - User-friendly labels via displayLabels.js
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Sparkles,
  ChevronRight,
  ChevronLeft,
  Check,
  AlertCircle,
  Info,
  Target,
  FileText,
  Loader2,
  X,
  Search,
  Filter,
  Zap,
  Shield,
  Scale,
  TrendingUp,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { cn } from '../../lib/utils';
import { useDisputeWorkflow, SEVERITY_COLORS, CLASSIFICATION_COLORS, CATEGORY_ICONS } from '../../hooks/useDisputeWorkflow';
import { AccountDetailsPanel, PaymentHistoryGrid, BureauComparisonSection } from '../credit-report';
import { getDisplayLabel, DISPLAY_LABELS } from '../../config';

// Step definitions
const STEPS = [
  { id: 'review', label: 'Review Account', icon: FileText },
  { id: 'reasons', label: 'Select Reasons', icon: Target },
  { id: 'strategy', label: 'Review Strategy', icon: Zap },
  { id: 'confirm', label: 'Confirm', icon: Check },
  { id: 'letter', label: 'Generate Letter', icon: FileText },
];

// Category display names and icons
const CATEGORY_DISPLAY = {
  identity: { label: 'Identity Issues', icon: Shield, color: 'text-red-600' },
  balance: { label: 'Balance & Amounts', icon: TrendingUp, color: 'text-amber-600' },
  dates: { label: 'Date Discrepancies', icon: FileText, color: 'text-emerald-600' },
  status: { label: 'Status Errors', icon: AlertCircle, color: 'text-orange-600' },
  payment_history: { label: 'Payment History', icon: TrendingUp, color: 'text-amber-600' },
  creditor: { label: 'Creditor Information', icon: FileText, color: 'text-slate-600' },
  terms: { label: 'Account Terms', icon: Scale, color: 'text-teal-600' },
  collection: { label: 'Collection Issues', icon: AlertCircle, color: 'text-red-500' },
  public_record: { label: 'Public Records', icon: Scale, color: 'text-amber-600' },
  inquiry: { label: 'Inquiry Issues', icon: Search, color: 'text-teal-600' },
  personal_info: { label: 'Personal Information', icon: Shield, color: 'text-pink-600' },
  compliance: { label: 'FCRA Compliance', icon: Scale, color: 'text-emerald-600' },
  consistency: { label: 'Bureau Consistency', icon: Filter, color: 'text-amber-600' },
  escalation: { label: 'Escalation', icon: TrendingUp, color: 'text-rose-600' },
};

// Get AI-suggested reasons based on account classification
const getAISuggestedReasons = (account, allReasons) => {
  const suggestions = [];
  const classification = account.classification || 'unknown';
  const isNegative = account.is_negative;
  const isCollection = account.is_collection;
  const accountStatus = account.account_status?.toLowerCase() || '';
  
  // Always suggest "not_my_account" for negative items
  if (isNegative) {
    const notMyAccount = allReasons.find(r => r.reason_key === 'not_my_account');
    if (notMyAccount) suggestions.push({ ...notMyAccount, ai_confidence: 'high', ai_reason: 'High-impact structural dispute' });
  }
  
  // Collection-specific suggestions
  if (isCollection || classification === 'collection') {
    const collectionReasons = ['debt_not_owed', 'validation_not_received', 'obsolete_debt', 'incorrect_collection_amount'];
    collectionReasons.forEach(key => {
      const reason = allReasons.find(r => r.reason_key === key);
      if (reason && !suggestions.find(s => s.reason_key === key)) {
        suggestions.push({ ...reason, ai_confidence: 'high', ai_reason: 'Common collection dispute' });
      }
    });
  }
  
  // Charge-off suggestions
  if (classification === 'charge_off' || accountStatus.includes('charge')) {
    const chargeOffReasons = ['incorrect_charge_off_date', 'charge_off_balance_wrong', 'paid_charge_off_still_showing'];
    chargeOffReasons.forEach(key => {
      const reason = allReasons.find(r => r.reason_key === key);
      if (reason && !suggestions.find(s => s.reason_key === key)) {
        suggestions.push({ ...reason, ai_confidence: 'medium', ai_reason: 'Charge-off specific error' });
      }
    });
  }
  
  // Late payment suggestions
  if (classification === 'late_payment' || account.has_late_payments) {
    const lateReasons = ['incorrect_late_status', 'wrong_payment_history', 'payment_not_credited'];
    lateReasons.forEach(key => {
      const reason = allReasons.find(r => r.reason_key === key);
      if (reason && !suggestions.find(s => s.reason_key === key)) {
        suggestions.push({ ...reason, ai_confidence: 'medium', ai_reason: 'Payment history dispute' });
      }
    });
  }
  
  // Balance-related suggestions if balance exists
  if (account.balance && account.balance > 0) {
    const balanceReason = allReasons.find(r => r.reason_key === 'incorrect_balance');
    if (balanceReason && !suggestions.find(s => s.reason_key === 'incorrect_balance')) {
      suggestions.push({ ...balanceReason, ai_confidence: 'medium', ai_reason: 'Balance verification opportunity' });
    }
  }
  
  // FCRA compliance suggestions for older accounts
  const dateOpened = account.date_opened;
  if (dateOpened) {
    const openDate = new Date(dateOpened);
    const yearsOld = (new Date() - openDate) / (365.25 * 24 * 60 * 60 * 1000);
    if (yearsOld > 6) {
      const obsoleteReason = allReasons.find(r => r.reason_key === 'obsolete_account');
      if (obsoleteReason && !suggestions.find(s => s.reason_key === 'obsolete_account')) {
        suggestions.push({ ...obsoleteReason, ai_confidence: 'low', ai_reason: 'Account age may exceed reporting period' });
      }
    }
  }
  
  return suggestions.slice(0, 5); // Return top 5 suggestions
};

// Reason Card Component
const ReasonCard = ({ reason, isSelected, onToggle, isAISuggested, aiConfidence, aiReason }) => {
  const severityColor = SEVERITY_COLORS[reason.severity] || SEVERITY_COLORS.medium;
  const classificationColor = CLASSIFICATION_COLORS[reason.classification] || CLASSIFICATION_COLORS.factual;
  
  return (
    <div
      onClick={() => onToggle(reason.reason_key)}
      className={cn(
        'p-4 rounded-xl border-2 cursor-pointer transition-all',
        isSelected 
          ? 'border-emerald-500 bg-emerald-50/50' 
          : 'border-slate-200 hover:border-slate-300 bg-white'
      )}
      data-testid={`reason-card-${reason.reason_key}`}
    >
      <div className="flex items-start gap-3">
        <div className={cn(
          'w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5',
          isSelected ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'
        )}>
          {isSelected && <Check size={12} className="text-white" />}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-medium text-stone-900">{reason.display_label}</span>
            {isAISuggested && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className={cn(
                      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                      aiConfidence === 'high' ? 'bg-amber-100 text-amber-700' :
                      aiConfidence === 'medium' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-slate-100 text-slate-600'
                    )}>
                      <Sparkles size={10} />
                      AI Suggested
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">{aiReason}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          
          <p className="text-sm text-slate-600 line-clamp-2 mb-2">
            {reason.instruction_text?.substring(0, 100)}...
          </p>
          
          <div className="flex items-center gap-2 flex-wrap">
            <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', severityColor.bg, severityColor.text)}>
              {reason.severity?.charAt(0).toUpperCase() + reason.severity?.slice(1)}
            </span>
            <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', classificationColor.bg, classificationColor.text)}>
              {reason.classification?.charAt(0).toUpperCase() + reason.classification?.slice(1)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Strategy Display Component
const StrategyDisplay = ({ workflow }) => {
  if (!workflow) return null;
  
  const { routing_strategy, template_family, prompt_keys, reason_details, validation_warnings } = workflow;
  
  return (
    <div className="space-y-4">
      {/* Routing Strategy */}
      <div className="p-4 rounded-xl bg-gradient-to-br from-amber-50 to-amber-50 border border-amber-200">
        <div className="flex items-center gap-2 mb-2">
          <Zap size={18} className="text-amber-600" />
          <span className="font-semibold text-amber-900">Routing Strategy</span>
        </div>
        <p className="text-lg font-medium text-amber-800">{routing_strategy?.key?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
        <p className="text-sm text-amber-600 mt-1">{routing_strategy?.description}</p>
      </div>
      
      {/* Template Family */}
      <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200">
        <div className="flex items-center gap-2 mb-2">
          <FileText size={18} className="text-emerald-600" />
          <span className="font-semibold text-emerald-900">Letter Template</span>
        </div>
        <p className="text-lg font-medium text-emerald-800">{template_family?.key?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
        <div className="flex items-center gap-4 mt-2 text-sm text-emerald-600">
          <span>Recipient: {template_family?.recipient?.replace(/_/g, ' ')}</span>
          <span>Round: {template_family?.round}</span>
          <span>Tone: {template_family?.tone?.replace(/_/g, ' ')}</span>
        </div>
      </div>
      
      {/* Prompt Keys */}
      <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200">
        <div className="flex items-center gap-2 mb-3">
          <Target size={18} className="text-emerald-600" />
          <span className="font-semibold text-emerald-900">Dispute Arguments ({prompt_keys?.length || 0})</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {prompt_keys?.map((pk) => (
            <TooltipProvider key={pk.key}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="px-3 py-1.5 bg-white rounded-lg border border-emerald-200 text-sm font-medium text-emerald-700 cursor-help">
                    {pk.key?.replace(/^pk_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-xs font-medium">{pk.description}</p>
                  <p className="text-xs text-slate-500 mt-1">Legal basis: {pk.legal_basis}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        </div>
      </div>
      
      {/* Validation Warnings */}
      {validation_warnings?.length > 0 && (
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
          <div className="flex items-center gap-2 text-amber-700">
            <AlertCircle size={16} />
            <span className="text-sm font-medium">Warnings</span>
          </div>
          <ul className="mt-2 space-y-1">
            {validation_warnings.map((warning, idx) => (
              <li key={idx} className="text-sm text-amber-600">{warning}</li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Selected Reasons Summary */}
      <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
        <div className="flex items-center gap-2 mb-3">
          <Check size={18} className="text-slate-600" />
          <span className="font-semibold text-stone-900">Selected Reasons ({reason_details?.length || 0})</span>
        </div>
        <div className="space-y-2">
          {reason_details?.map((reason) => (
            <div key={reason.reason_key} className="flex items-center gap-2 text-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-slate-700">{reason.display_label}</span>
              <span className="text-slate-400">({reason.category})</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Account Review Display using canonical field names
const AccountReviewDisplay = ({ account }) => {
  const displayFields = [
    { key: 'account_name', fallback: 'creditor_name' },
    { key: 'account_number_masked', fallback: 'masked_account_number' },
    { key: 'bureau_code', fallback: 'bureau' },
    { key: 'account_type' },
    { key: 'account_status', fallback: 'status' },
    { key: 'balance', format: 'currency' },
    { key: 'credit_limit', fallback: 'limit', format: 'currency' },
    { key: 'date_opened', format: 'date' },
    { key: 'last_reported', fallback: 'date_reported', format: 'date' },
    { key: 'payment_status' },
    { key: 'original_creditor' },
  ];
  
  const formatValue = (value, format) => {
    if (value === null || value === undefined || value === '') return 'Not reported';
    if (format === 'currency') {
      return typeof value === 'number' ? `$${value.toLocaleString()}` : value;
    }
    if (format === 'date') {
      return value;
    }
    return String(value);
  };
  
  const getValue = (field) => {
    const value = account[field.key] ?? (field.fallback ? account[field.fallback] : undefined);
    return formatValue(value, field.format);
  };
  
  return (
    <div className="space-y-4">
      {/* Account Header */}
      <div className="p-4 rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-xl font-bold text-stone-900">
              {account.account_name || account.creditor_name || 'Unknown Account'}
            </h3>
            <p className="text-slate-500">
              {account.account_number_masked || account.masked_account_number || '****'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {account.is_negative && (
              <Badge variant="destructive" className="bg-red-100 text-red-700 border-red-200">
                Negative Item
              </Badge>
            )}
            {account.is_collection && (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                Collection
              </Badge>
            )}
          </div>
        </div>
      </div>
      
      {/* Account Details Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {displayFields.map((field) => {
          const value = getValue(field);
          if (value === 'Not reported') return null;
          
          return (
            <div key={field.key} className="p-3 rounded-lg bg-white border border-slate-200">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                {getDisplayLabel(field.key)}
              </p>
              <p className="text-sm font-semibold text-stone-900 mt-1">{value}</p>
            </div>
          );
        })}
      </div>
      
      {/* Classification Info */}
      {account.classification && (
        <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
          <div className="flex items-center gap-2">
            <Info size={16} className="text-emerald-600" />
            <span className="text-sm text-emerald-700">
              <strong>Classification:</strong> {account.classification?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

// Main DisputeWizard Component
export const DisputeWizard = ({ open, onClose, account, onSubmit, token }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [disputeRound, setDisputeRound] = useState(1);
  const [createdDispute, setCreatedDispute] = useState(null);
  const [generatedLetter, setGeneratedLetter] = useState(null);
  const [letterLoading, setLetterLoading] = useState(false);
  const [letterError, setLetterError] = useState(null);
  
  const API = process.env.REACT_APP_BACKEND_URL;
  
  const {
    loading,
    error,
    currentWorkflow,
    allReasons,
    categories,
    resolveWorkflow,
    clearWorkflow,
  } = useDisputeWorkflow();
  
  // Get AI suggestions
  const aiSuggestions = useMemo(() => {
    if (!account) return [];
    return getAISuggestedReasons(account, allReasons);
  }, [account, allReasons]);
  
  // Filter reasons
  const filteredReasons = useMemo(() => {
    let filtered = allReasons;
    
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(r => r.category === selectedCategory);
    }
    
    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => 
        r.display_label?.toLowerCase().includes(query) ||
        r.instruction_text?.toLowerCase().includes(query) ||
        r.reason_key?.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [allReasons, selectedCategory, searchQuery]);
  
  // Group reasons by category for display
  const reasonsByCategory = useMemo(() => {
    const grouped = {};
    filteredReasons.forEach(reason => {
      const cat = reason.category || 'other';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(reason);
    });
    return grouped;
  }, [filteredReasons]);
  
  // Toggle reason selection
  const toggleReason = useCallback((reasonKey) => {
    setSelectedReasons(prev => 
      prev.includes(reasonKey) 
        ? prev.filter(k => k !== reasonKey)
        : [...prev, reasonKey]
    );
  }, []);
  
  // Resolve workflow when reasons change
  useEffect(() => {
    if (selectedReasons.length > 0 && account && token && currentStep >= 2) {
      resolveWorkflow(account, selectedReasons, disputeRound, null, token);
    }
  }, [selectedReasons, account, token, disputeRound, currentStep]);
  
  // Handle next step
  const handleNext = () => {
    if (currentStep === 1 && selectedReasons.length === 0) {
      return; // Can't proceed without selecting reasons
    }
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };
  
  // Handle back
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  // Handle submit - creates dispute and moves to letter generation
  const handleSubmit = async () => {
    if (!currentWorkflow) return;
    
    try {
      const disputeData = {
        tradeline_id: account.tradeline_id,
        selected_reasons: selectedReasons,
        routing_strategy: currentWorkflow.routing_strategy?.key,
        template_family: currentWorkflow.template_family?.key,
        prompt_keys: currentWorkflow.prompt_keys?.map(pk => pk.key),
        dispute_round: disputeRound,
      };
      
      const result = await onSubmit(disputeData);
      setCreatedDispute(result);
      
      // Move to letter generation step
      setCurrentStep(4);
    } catch (err) {
      console.error('Failed to create dispute:', err);
    }
  };
  
  // Handle letter generation
  const handleGenerateLetter = async () => {
    if (!createdDispute?.dispute_id) return;
    
    setLetterLoading(true);
    setLetterError(null);
    
    try {
      const response = await fetch(`${API}/api/disputes/${createdDispute.dispute_id}/generate-letter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          dispute_id: createdDispute.dispute_id,
          letter_type: currentWorkflow?.template_family?.recipient === 'creditor' ? 'creditor' : 'bureau',
          use_workflow: true,
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate letter');
      }
      
      const letter = await response.json();
      setGeneratedLetter(letter);
    } catch (err) {
      console.error('Letter generation error:', err);
      setLetterError(err.message || 'Failed to generate letter');
    } finally {
      setLetterLoading(false);
    }
  };
  
  // Handle finish and close
  const handleFinish = () => {
    handleClose();
  };
  
  // Reset on close
  const handleClose = () => {
    setCurrentStep(0);
    setSelectedReasons([]);
    setSearchQuery('');
    setSelectedCategory('all');
    setCreatedDispute(null);
    setGeneratedLetter(null);
    setLetterLoading(false);
    setLetterError(null);
    clearWorkflow();
    onClose();
  };
  
  // Check if reason is AI suggested
  const isAISuggested = (reasonKey) => {
    return aiSuggestions.some(s => s.reason_key === reasonKey);
  };
  
  const getAISuggestionDetails = (reasonKey) => {
    return aiSuggestions.find(s => s.reason_key === reasonKey);
  };
  
  if (!account) return null;
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        {/* Header */}
        <DialogHeader className="p-6 pb-4 border-b border-slate-200">
          <DialogTitle className="text-xl">Create Dispute</DialogTitle>
          <DialogDescription>
            {account.account_name || account.creditor_name} - Step {currentStep + 1} of {STEPS.length}
          </DialogDescription>
          
          {/* Progress Steps */}
          <div className="flex items-center gap-2 mt-4">
            {STEPS.map((step, idx) => {
              const StepIcon = step.icon;
              const isActive = idx === currentStep;
              const isCompleted = idx < currentStep;
              
              return (
                <React.Fragment key={step.id}>
                  <div className={cn(
                    'flex items-center gap-2 px-3 py-2 rounded-lg transition-all',
                    isActive ? 'bg-emerald-100 text-emerald-700' :
                    isCompleted ? 'bg-emerald-500 text-white' :
                    'bg-slate-100 text-slate-400'
                  )}>
                    <StepIcon size={16} />
                    <span className="text-sm font-medium hidden sm:inline">{step.label}</span>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <ChevronRight size={16} className="text-slate-300" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </DialogHeader>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 0: Review Account - Bureau Comparison FIRST */}
          {currentStep === 0 && (
            <div className="space-y-6">
              {/* Bureau Comparison Table - MAIN VIEW for dispute-relevant transparency */}
              {/* Shows all fields with per-bureau values, highlights differences */}
              <BureauComparisonSection
                tradeline={account}
                accountName={account?.account_name || account?.creditor_name}
              />
              
              {/* Payment History Grid - Separate section */}
              <PaymentHistoryGrid account={account} defaultMonths={12} />
            </div>
          )}
          
          {/* Step 1: Select Reasons */}
          {currentStep === 1 && (
            <div className="space-y-4">
              {/* AI Suggestions Section */}
              {aiSuggestions.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles size={18} className="text-amber-500" />
                    <span className="font-semibold text-stone-900">AI Suggested Reasons</span>
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      Based on account analysis
                    </Badge>
                  </div>
                  <div className="grid gap-3">
                    {aiSuggestions.map((reason) => (
                      <ReasonCard
                        key={reason.reason_key}
                        reason={reason}
                        isSelected={selectedReasons.includes(reason.reason_key)}
                        onToggle={toggleReason}
                        isAISuggested={true}
                        aiConfidence={reason.ai_confidence}
                        aiReason={reason.ai_reason}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Search and Filter */}
              <div className="flex items-center gap-3 sticky top-0 bg-white py-2 z-10">
                <div className="relative flex-1">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search reasons..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="reason-search"
                  />
                </div>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                  data-testid="category-filter"
                >
                  <option value="all">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>
                      {CATEGORY_DISPLAY[cat]?.label || cat}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Selected Count */}
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-slate-600">
                  {selectedReasons.length} reason{selectedReasons.length !== 1 ? 's' : ''} selected
                </span>
                {selectedReasons.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedReasons([])}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    Clear all
                  </Button>
                )}
              </div>
              
              {/* Reasons by Category */}
              <div className="space-y-6">
                {Object.entries(reasonsByCategory).map(([category, reasons]) => {
                  const categoryInfo = CATEGORY_DISPLAY[category] || { label: category, icon: FileText, color: 'text-slate-600' };
                  const CategoryIcon = categoryInfo.icon;
                  
                  return (
                    <div key={category}>
                      <div className="flex items-center gap-2 mb-3">
                        <CategoryIcon size={16} className={categoryInfo.color} />
                        <span className="font-medium text-slate-700">{categoryInfo.label}</span>
                        <span className="text-xs text-slate-400">({reasons.length})</span>
                      </div>
                      <div className="grid gap-3">
                        {reasons.map((reason) => {
                          const suggestionDetails = getAISuggestionDetails(reason.reason_key);
                          return (
                            <ReasonCard
                              key={reason.reason_key}
                              reason={reason}
                              isSelected={selectedReasons.includes(reason.reason_key)}
                              onToggle={toggleReason}
                              isAISuggested={!!suggestionDetails}
                              aiConfidence={suggestionDetails?.ai_confidence}
                              aiReason={suggestionDetails?.ai_reason}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Step 2: Review Strategy */}
          {currentStep === 2 && (
            <div className="space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                  <span className="ml-3 text-slate-600">Resolving dispute strategy...</span>
                </div>
              ) : error ? (
                <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700">
                  <AlertCircle size={18} className="inline mr-2" />
                  {error}
                </div>
              ) : (
                <StrategyDisplay workflow={currentWorkflow} />
              )}
            </div>
          )}
          
          {/* Step 3: Confirm */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Check size={32} className="text-emerald-600" />
                </div>
                <h3 className="text-xl font-bold text-emerald-900 mb-2">Ready to Create Dispute</h3>
                <p className="text-emerald-700">
                  Your dispute will be created with {selectedReasons.length} reason{selectedReasons.length !== 1 ? 's' : ''} 
                  using the <strong>{currentWorkflow?.routing_strategy?.key?.replace(/_/g, ' ')}</strong> strategy.
                </p>
              </div>
              
              {/* Summary */}
              <div className="grid gap-4">
                <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                  <p className="text-sm font-medium text-slate-500 mb-1">Account</p>
                  <p className="font-semibold text-stone-900">
                    {account.account_name || account.creditor_name}
                  </p>
                </div>
                
                <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                  <p className="text-sm font-medium text-slate-500 mb-1">Target</p>
                  <p className="font-semibold text-stone-900">
                    {currentWorkflow?.template_family?.recipient === 'credit_bureau' ? 'Credit Bureau' : 'Creditor'} 
                    {' - '}
                    {account.bureau_code || account.bureau || 'All Bureaus'}
                  </p>
                </div>
                
                <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                  <p className="text-sm font-medium text-slate-500 mb-1">Dispute Round</p>
                  <p className="font-semibold text-stone-900">Round {disputeRound}</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Step 4: Generate Letter */}
          {currentStep === 4 && (
            <div className="space-y-6">
              {/* Success message for dispute creation */}
              {createdDispute && !generatedLetter && !letterLoading && (
                <div className="p-6 rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Check size={32} className="text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-emerald-900 mb-2">Dispute Created Successfully!</h3>
                  <p className="text-emerald-700 mb-4">
                    Dispute ID: <code className="bg-emerald-100 px-2 py-0.5 rounded">{createdDispute.dispute_id}</code>
                  </p>
                  <p className="text-slate-600">
                    Now let's generate your dispute letter using the resolved strategy and arguments.
                  </p>
                </div>
              )}
              
              {/* Letter generation loading */}
              {letterLoading && (
                <div className="p-8 rounded-xl bg-slate-50 border border-slate-200 text-center">
                  <Loader2 className="w-12 h-12 animate-spin text-emerald-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-stone-900 mb-2">Generating Your Letter...</h3>
                  <p className="text-slate-600">
                    AI is crafting a professional dispute letter using your selected arguments and legal citations.
                  </p>
                </div>
              )}
              
              {/* Letter error */}
              {letterError && (
                <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-700">
                  <AlertCircle size={18} className="inline mr-2" />
                  {letterError}
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-4"
                    onClick={handleGenerateLetter}
                  >
                    Try Again
                  </Button>
                </div>
              )}
              
              {/* Generated letter display */}
              {generatedLetter && (
                <div className="space-y-4">
                  {/* Letter metadata */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                        <FileText size={20} className="text-emerald-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-stone-900">Dispute Letter Generated</p>
                        <p className="text-sm text-slate-500">
                          {generatedLetter.generation_method === 'ai' ? 'AI-generated' : 'Template-based'} • 
                          {generatedLetter.template_family?.replace(/_/g, ' ')} • 
                          Round {disputeRound}
                        </p>
                      </div>
                    </div>
                    <Badge 
                      variant="outline" 
                      className={generatedLetter.generation_method === 'ai' 
                        ? 'bg-amber-50 text-amber-700 border-amber-200' 
                        : 'bg-slate-50 text-slate-700 border-slate-200'
                      }
                    >
                      {generatedLetter.generation_method === 'ai' ? (
                        <>
                          <Sparkles size={12} className="mr-1" />
                          AI Generated
                        </>
                      ) : 'Template'}
                    </Badge>
                  </div>
                  
                  {/* Letter content */}
                  <div className="p-6 bg-white rounded-xl border border-slate-200 max-h-96 overflow-y-auto">
                    <pre className="whitespace-pre-wrap font-serif text-sm text-stone-800 leading-relaxed">
                      {generatedLetter.content}
                    </pre>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-sm text-slate-600">
                      You can view, edit, or mail this letter from the Disputes page.
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleGenerateLetter}
                      >
                        Regenerate
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Generate button if no letter yet */}
              {createdDispute && !generatedLetter && !letterLoading && !letterError && (
                <div className="text-center">
                  <Button
                    onClick={handleGenerateLetter}
                    className="btn-primary"
                    data-testid="generate-letter-btn"
                  >
                    <Sparkles size={16} className="mr-2" />
                    Generate Dispute Letter
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <DialogFooter className="p-6 pt-4 border-t border-slate-200 flex-shrink-0">
          <div className="flex items-center justify-between w-full">
            <Button
              variant="outline"
              onClick={currentStep === 0 || currentStep === 4 ? handleClose : handleBack}
              disabled={letterLoading}
              data-testid="wizard-back-btn"
            >
              {currentStep === 0 ? 'Cancel' : currentStep === 4 ? 'Close' : (
                <>
                  <ChevronLeft size={16} className="mr-1" />
                  Back
                </>
              )}
            </Button>
            
            <div className="flex items-center gap-3">
              {currentStep === 1 && selectedReasons.length === 0 && (
                <span className="text-sm text-amber-600">Select at least one reason</span>
              )}
              
              {currentStep < 3 && (
                <Button
                  onClick={handleNext}
                  disabled={currentStep === 1 && selectedReasons.length === 0}
                  className="btn-primary"
                  data-testid="wizard-next-btn"
                >
                  Next
                  <ChevronRight size={16} className="ml-1" />
                </Button>
              )}
              
              {currentStep === 3 && (
                <Button
                  onClick={handleSubmit}
                  disabled={loading || !currentWorkflow}
                  className="btn-primary"
                  data-testid="wizard-submit-btn"
                >
                  {loading ? (
                    <Loader2 size={16} className="animate-spin mr-2" />
                  ) : (
                    <Check size={16} className="mr-2" />
                  )}
                  Create Dispute
                </Button>
              )}
              
              {currentStep === 4 && generatedLetter && (
                <Button
                  onClick={handleFinish}
                  className="btn-primary"
                  data-testid="wizard-finish-btn"
                >
                  <Check size={16} className="mr-2" />
                  Done
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DisputeWizard;
